import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Subtle "Updated X seconds ago" chip, driven by useAutoRefresh.
 *
 * - Ticks its own text every 10s so users see the counter tick up
 *   even when no refresh has fired.
 * - Shows a spinning icon while `isRefreshing`.
 * - Muted by design — meant to sit in the corner of a header/toolbar,
 *   not steal attention. Colour switches to green briefly right after
 *   a refresh so the eye catches "something just updated".
 */
export default function LastUpdatedIndicator({
  lastRefreshedAt,
  isRefreshing,
  className = '',
  compact = false,
}) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => (v + 1) % 1_000_000), 10_000);
    return () => clearInterval(t);
  }, []);

  let text;
  const flashJustUpdated = lastRefreshedAt && Date.now() - lastRefreshedAt < 4_000;
  if (!lastRefreshedAt) {
    text = compact ? 'Live' : 'Updating live';
  } else {
    const secs = Math.max(0, Math.floor((Date.now() - lastRefreshedAt) / 1000));
    if (secs < 5) text = 'Updated just now';
    else if (secs < 60) text = `Updated ${secs}s ago`;
    else text = `Updated ${Math.floor(secs / 60)}m ago`;
  }
  // silence the unused-var lint — `tick` is only there to force a re-render
  void tick;

  const flashClass = flashJustUpdated ? 'text-emerald-700' : 'text-gray-500';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] ${flashClass} ${className}`}
      data-testid="auto-refresh-indicator"
      title={lastRefreshedAt ? new Date(lastRefreshedAt).toLocaleTimeString() : ''}
    >
      <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
      <span>{text}</span>
    </span>
  );
}
